import type { EnvironmentRef, SectionSummary, Variable, VariableVersion, VersionChange } from '@fleequid-secrets/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useState } from 'react';
import { api, messageOf } from '@/api/client';
import { sentence } from '@/lib/activity';
import { copyText } from '@/lib/clipboard';
import { ago, stamp } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { Cancelled, guarded, useShell } from '@/lib/shell';
import { Icon } from './Icon';

const CHANGE_WORDS: Readonly<Record<VersionChange, string>> = {
    created: 'Added',
    updated: 'Changed',
    restored: 'Restored',
    imported: 'Imported',
    copied: 'Copied in',
};

// A variable's history beside its section: every value it has had (each revealed on demand, the reveal recorded),
// the way back to any of them, and who looked at it.
export function HistorySheet(props: {
    environment: EnvironmentRef;
    section: SectionSummary;
    variable: Variable;
    onClose: () => void;
    onRestored: () => void;
}): ReactNode {
    const shell = useShell();
    const queryClient = useQueryClient();
    const now = useNow(30_000);
    const versions = useQuery({ queryKey: ['versions', props.variable.id, props.variable.version], queryFn: () => api.versions(props.variable.id) });
    const activity = useQuery({
        queryKey: ['activity', { variable: props.variable.id }, props.variable.version],
        queryFn: () => api.activity({ variable: props.variable.id, limit: 30 }),
    });
    // Versions revealed here; the sheet is keyed by its variable, so another variable starts with none.
    const [shown, setShown] = useState<ReadonlyMap<number, string>>(new Map());

    const reveal = (version: VariableVersion): void => {
        if (shown.has(version.version)) {
            setShown(new Map([...shown].filter(([key]) => key !== version.version)));

            return;
        }
        api.revealVersion(props.variable.id, version.version, 'reveal')
            .then(({ value }) => setShown((current) => new Map(current).set(version.version, value)))
            .then(() => queryClient.invalidateQueries({ queryKey: ['activity'] }))
            .catch((error: unknown) => shell.say(messageOf(error)));
    };
    const copy = (version: VariableVersion): void => {
        api.revealVersion(props.variable.id, version.version, 'copy')
            .then(({ value }) => copyText(value))
            .then(() => shell.say(`Copied version ${version.version} of ${props.variable.key}`))
            .then(() => queryClient.invalidateQueries({ queryKey: ['activity'] }))
            .catch((error: unknown) => shell.say(messageOf(error)));
    };
    const restore = (version: VariableVersion): void => {
        guarded(shell, props.environment, `restore version ${version.version} of ${props.variable.key}`, (confirm) =>
            api.restoreVersion(props.variable.id, version.version, confirm),
        )
            .then(({ variable }) => {
                shell.say(`${props.variable.key} is back to version ${version.version}, now version ${variable.version}`);
                props.onRestored();
            })
            .catch((error: unknown) => !(error instanceof Cancelled) && shell.say(messageOf(error)));
    };

    const looks = (activity.data?.events ?? []).filter((event) =>
        ['variable.revealed', 'variable.copied', 'version.revealed', 'section.revealed'].includes(event.action),
    );

    return (
        <aside className="sheet" onKeyDown={(event) => event.key === 'Escape' && props.onClose()}>
            <div className="sheet-top">
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="crumb">
                        <i className="dot" style={{ '--c': props.environment.color } as CSSProperties} />
                        {props.environment.name} · {props.section.name}
                    </div>
                    <h2>{props.variable.key}</h2>
                    {props.variable.note && <p>{props.variable.note}</p>}
                </div>
                <button className="closebtn" onClick={props.onClose} title="Close (esc)">
                    <Icon name="x" size={17} />
                </button>
            </div>
            <div className="sheet-body">
                <h3>Versions</h3>
                <div className="timeline">
                    {(versions.data?.versions ?? []).map((version) => {
                        const current = version.version === props.variable.version;
                        const value = shown.get(version.version);

                        return (
                            <div key={version.version} className={`titem ${current ? 'current' : ''}`}>
                                <i />
                                <div>
                                    <div className="what">
                                        <b>Version {version.version}</b> · {CHANGE_WORDS[version.change]}
                                        {version.createdBy ? ` by ${version.createdBy.name}` : ''}
                                        {current && ' · current'}
                                    </div>
                                    <div className="when" title={stamp(version.createdAt)}>
                                        {ago(version.createdAt, now)}
                                    </div>
                                </div>
                                <div className="tacts">
                                    <button
                                        className={`iconbtn ${value !== undefined ? 'on' : ''}`}
                                        title="Reveal this version"
                                        onClick={() => reveal(version)}
                                    >
                                        <Icon name={value !== undefined ? 'eyeoff' : 'eye'} size={16} />
                                    </button>
                                    <button className="iconbtn" title="Copy this version" onClick={() => copy(version)}>
                                        <Icon name="copy" size={16} />
                                    </button>
                                    {!current && (
                                        <button className="iconbtn" title="Bring this value back, as a new version" onClick={() => restore(version)}>
                                            <Icon name="undo" size={16} />
                                        </button>
                                    )}
                                </div>
                                {value !== undefined && <div className="value">{value === '' ? 'empty' : value}</div>}
                            </div>
                        );
                    })}
                </div>
                <h3>Who looked</h3>
                {looks.length === 0 ? (
                    <div className="micro">Nobody has revealed or copied it yet.</div>
                ) : (
                    <div className="timeline">
                        {looks.map((event) => {
                            const words = sentence(event);

                            return (
                                <div key={event.id} className="titem">
                                    <i />
                                    <div>
                                        <div className="what">
                                            <b>{words.who}</b> {words.verb}
                                            {words.extra ? ` · ${words.extra}` : ''}
                                        </div>
                                        <div className="when" title={stamp(event.at)}>
                                            {ago(event.at, now)}
                                            {event.ip ? ` · ${event.ip}` : ''}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </aside>
    );
}
